/**
 * TRACKING SYSTEM - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  Screen,
  Panel,
  Section,
  Button,
  Badge,
  Input,
  Select,
  Textarea,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  Tabs,
} from '../design/primitives';
import { SPACING, COLORS, TYPOGRAPHY } from '../design/tokens';
import { ideasApi, bugsApi, featuresApi, researchApi, Idea, Bug, Feature, Research } from '../api/tracking';
import { Alert } from '../components/ui';
import { ImageUpload } from '../components/forms/ImageUpload';

type TabType = 'ideas' | 'bugs' | 'features' | 'research';

const TrackingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('ideas');

  return (
    <Screen>
      {/* Header */}
      <Section>
        <h1
          style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[8],
          }}
        >
          Tracking System
        </h1>
        <p
          style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            margin: 0,
          }}
        >
          Bug reports, feature documentation, research findings, improvement ideas
        </p>
      </Section>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'ideas', label: 'Ideas' },
          { id: 'bugs', label: 'Bugs' },
          { id: 'features', label: 'Features' },
          { id: 'research', label: 'Research' },
        ]}
        activeTab={activeTab}
        onChange={(tab) => setActiveTab(tab as TabType)}
      />

      {/* Tab Content */}
      {activeTab === 'ideas' && <IdeasTab />}
      {activeTab === 'bugs' && <BugsTab />}
      {activeTab === 'features' && <FeaturesTab />}
      {activeTab === 'research' && <ResearchTab />}
    </Screen>
  );
};

// ============================================
// IDEAS TAB
// ============================================
function IdeasTab() {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingIdea, setEditingIdea] = useState<Partial<Idea> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchIdeas = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ideasApi.getAll({ search: searchTerm || undefined });
      setIdeas(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingIdea({
      title: '',
      summary: '',
      category: 'other',
      priority: 'medium',
      status: 'proposed',
    });
    setImageFile(null);
    setShowForm(true);
  };

  const handleEdit = (idea: Idea) => {
    setEditingIdea(idea);
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await ideasApi.delete(id);
      fetchIdeas();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingIdea) return;
    try {
      const ideaData = { ...editingIdea, image: imageFile };
      if (editingIdea.id) {
        await ideasApi.update(editingIdea.id, ideaData);
      } else {
        await ideasApi.create(ideaData as Idea);
      }
      setShowForm(false);
      setEditingIdea(null);
      setImageFile(null);
      fetchIdeas();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  };

  const getPriorityBadge = (priority: string) => {
    const colors: any = {
      critical: 'error',
      high: 'warning',
      medium: 'accent',
      low: 'default',
      someday: 'default',
    };
    return <Badge color={colors[priority] || 'default'}>{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      proposed: 'default',
      approved: 'accent',
      in_progress: 'warning',
      implemented: 'success',
      rejected: 'error',
      deferred: 'default',
    };
    return <Badge color={colors[status] || 'default'}>{status}</Badge>;
  };

  return (
    <>
      {/* Error */}
      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {/* Search & Actions */}
      <div style={{ display: 'flex', gap: SPACING[12], marginBottom: SPACING[16] }}>
        <div style={{ flex: 1 }}>
          <Input
            placeholder="Search ideas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
          />
        </div>
        <Button variant="secondary" onClick={fetchIdeas}>
          Search
        </Button>
        <Button variant="primary" onClick={handleCreate} icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}>
          New Idea
        </Button>
      </div>

      {/* Table */}
      <Panel noPadding>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Title</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Priority</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader align="right">Actions</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>Loading...</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : ideas.length === 0 ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>No ideas found</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              ideas.map((idea) => (
                <TableRow key={idea.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{idea.title}</div>
                    {idea.summary && (
                      <div
                        style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          marginTop: SPACING[4],
                        }}
                      >
                        {idea.summary.slice(0, 60)}
                        {idea.summary.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={{ textTransform: 'capitalize', color: COLORS.text.secondary }}>
                      {idea.category}
                    </span>
                  </TableCell>
                  <TableCell>{getPriorityBadge(idea.priority)}</TableCell>
                  <TableCell>{getStatusBadge(idea.status || 'proposed')}</TableCell>
                  <TableCell>
                    <span style={{ color: COLORS.text.tertiary }}>
                      {new Date(idea.created_at!).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(idea)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.text.secondary,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(idea.id!)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.status.error,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Panel>

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingIdea(null);
        }}
        title={editingIdea?.id ? 'Edit Idea' : 'New Idea'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Title
            </label>
            <Input
              value={editingIdea?.title || ''}
              onChange={(e) => setEditingIdea({ ...editingIdea, title: e.target.value })}
              placeholder="Brief title"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}
              >
                Category
              </label>
              <Select
                value={editingIdea?.category || 'other'}
                onChange={(e) => setEditingIdea({ ...editingIdea, category: e.target.value as any })}
                options={[
                  { value: 'algorithm', label: 'Algorithm' },
                  { value: 'ui', label: 'UI' },
                  { value: 'performance', label: 'Performance' },
                  { value: 'data_quality', label: 'Data Quality' },
                  { value: 'ux', label: 'UX' },
                  { value: 'infrastructure', label: 'Infrastructure' },
                  { value: 'integration', label: 'Integration' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}
              >
                Priority
              </label>
              <Select
                value={editingIdea?.priority || 'medium'}
                onChange={(e) => setEditingIdea({ ...editingIdea, priority: e.target.value as any })}
                options={[
                  { value: 'critical', label: 'Critical' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' },
                  { value: 'someday', label: 'Someday' },
                ]}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Summary
            </label>
            <Textarea
              value={editingIdea?.summary || ''}
              onChange={(e) => setEditingIdea({ ...editingIdea, summary: e.target.value })}
              placeholder="Brief summary of the idea"
              rows={3}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Screenshot (Optional)
            </label>
            <ImageUpload
              value={imageFile || editingIdea?.image_url || null}
              onChange={setImageFile}
              onPaste={() => {}}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Problem
            </label>
            <Textarea
              value={editingIdea?.problem || ''}
              onChange={(e) => setEditingIdea({ ...editingIdea, problem: e.target.value })}
              placeholder="What problem does this solve?"
              rows={2}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Proposed Solution
            </label>
            <Textarea
              value={editingIdea?.proposed_solution || ''}
              onChange={(e) => setEditingIdea({ ...editingIdea, proposed_solution: e.target.value })}
              placeholder="How would you solve it?"
              rows={2}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}

// ============================================
// BUGS TAB
// ============================================
function BugsTab() {
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBug, setEditingBug] = useState<Partial<Bug> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchBugs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bugsApi.getAll({ search: searchTerm || undefined });
      setBugs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load bugs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingBug({
      title: '',
      description: '',
      severity: 'moderate',
      status: 'open',
    });
    setImageFile(null);
    setShowForm(true);
  };

  const handleEdit = (bug: Bug) => {
    setEditingBug(bug);
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this bug?')) return;
    try {
      await bugsApi.delete(id);
      fetchBugs();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBug) return;
    try {
      const bugData = { ...editingBug, image: imageFile };
      if (editingBug.id) {
        await bugsApi.update(editingBug.id, bugData);
      } else {
        await bugsApi.create(bugData as Bug);
      }
      setShowForm(false);
      setEditingBug(null);
      setImageFile(null);
      fetchBugs();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  };

  const getSeverityBadge = (severity: string) => {
    const colors: any = {
      critical: 'error',
      major: 'warning',
      moderate: 'accent',
      minor: 'default',
    };
    return <Badge color={colors[severity] || 'default'}>{severity}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      open: 'error',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'success',
      wont_fix: 'default',
    };
    return <Badge color={colors[status] || 'default'}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <>
      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <div style={{ display: 'flex', gap: SPACING[12], marginBottom: SPACING[16] }}>
        <div style={{ flex: 1 }}>
          <Input
            placeholder="Search bugs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
          />
        </div>
        <Button variant="secondary" onClick={fetchBugs}>
          Search
        </Button>
        <Button variant="primary" onClick={handleCreate} icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}>
          New Bug
        </Button>
      </div>

      <Panel noPadding>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Title</TableHeader>
              <TableHeader>Severity</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Reported</TableHeader>
              <TableHeader align="right">Actions</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>Loading...</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : bugs.length === 0 ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>No bugs found</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              bugs.map((bug) => (
                <TableRow key={bug.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{bug.title}</div>
                    {bug.description && (
                      <div
                        style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          marginTop: SPACING[4],
                        }}
                      >
                        {bug.description.slice(0, 60)}
                        {bug.description.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{getSeverityBadge(bug.severity)}</TableCell>
                  <TableCell>{getStatusBadge(bug.status || 'open')}</TableCell>
                  <TableCell>
                    <span style={{ color: COLORS.text.tertiary }}>
                      {new Date(bug.reported_at || bug.created_at!).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(bug)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.text.secondary,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(bug.id!)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.status.error,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Panel>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingBug(null);
        }}
        title={editingBug?.id ? 'Edit Bug' : 'New Bug'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Title
            </label>
            <Input
              value={editingBug?.title || ''}
              onChange={(e) => setEditingBug({ ...editingBug, title: e.target.value })}
              placeholder="Brief description of the bug"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}
              >
                Severity
              </label>
              <Select
                value={editingBug?.severity || 'moderate'}
                onChange={(e) => setEditingBug({ ...editingBug, severity: e.target.value as any })}
                options={[
                  { value: 'critical', label: 'Critical' },
                  { value: 'major', label: 'Major' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'minor', label: 'Minor' },
                ]}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[8],
                }}
              >
                Status
              </label>
              <Select
                value={editingBug?.status || 'open'}
                onChange={(e) => setEditingBug({ ...editingBug, status: e.target.value as any })}
                options={[
                  { value: 'open', label: 'Open' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'resolved', label: 'Resolved' },
                  { value: 'closed', label: 'Closed' },
                  { value: 'wont_fix', label: "Won't Fix" },
                ]}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Description
            </label>
            <Textarea
              value={editingBug?.description || ''}
              onChange={(e) => setEditingBug({ ...editingBug, description: e.target.value })}
              placeholder="What is the bug? How to reproduce?"
              rows={3}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Screenshot (Optional)
            </label>
            <ImageUpload
              value={imageFile || editingBug?.image_url || null}
              onChange={setImageFile}
              onPaste={() => {}}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Root Cause
            </label>
            <Textarea
              value={editingBug?.root_cause || ''}
              onChange={(e) => setEditingBug({ ...editingBug, root_cause: e.target.value })}
              placeholder="What caused the bug?"
              rows={2}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Fix Applied
            </label>
            <Textarea
              value={editingBug?.fix || ''}
              onChange={(e) => setEditingBug({ ...editingBug, fix: e.target.value })}
              placeholder="How was it fixed?"
              rows={2}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}

// ============================================
// FEATURES TAB (simplified for brevity)
// ============================================
function FeaturesTab() {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Partial<Feature> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchFeatures = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await featuresApi.getAll({ search: searchTerm || undefined });
      setFeatures(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingFeature({
      name: '',
      description: '',
      expected_behavior: '',
      status: 'active',
    });
    setImageFile(null);
    setShowForm(true);
  };

  const handleEdit = (feature: Feature) => {
    setEditingFeature(feature);
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this feature?')) return;
    try {
      await featuresApi.delete(id);
      fetchFeatures();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFeature) return;
    try {
      const featureData = { ...editingFeature, image: imageFile };
      if (editingFeature.id) {
        await featuresApi.update(editingFeature.id, featureData);
      } else {
        await featuresApi.create(featureData as Feature);
      }
      setShowForm(false);
      setEditingFeature(null);
      setImageFile(null);
      fetchFeatures();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      active: 'success',
      deprecated: 'error',
      planned: 'accent',
    };
    return <Badge color={colors[status] || 'default'}>{status}</Badge>;
  };

  return (
    <>
      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <div style={{ display: 'flex', gap: SPACING[12], marginBottom: SPACING[16] }}>
        <div style={{ flex: 1 }}>
          <Input
            placeholder="Search features..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
          />
        </div>
        <Button variant="secondary" onClick={fetchFeatures}>
          Search
        </Button>
        <Button variant="primary" onClick={handleCreate} icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}>
          New Feature
        </Button>
      </div>

      <Panel noPadding>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader align="right">Actions</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>Loading...</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : features.length === 0 ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>No features found</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              features.map((feature) => (
                <TableRow key={feature.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{feature.name}</div>
                    {feature.description && (
                      <div
                        style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          marginTop: SPACING[4],
                        }}
                      >
                        {feature.description.slice(0, 60)}
                        {feature.description.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={{ textTransform: 'capitalize', color: COLORS.text.secondary }}>
                      {feature.category || 'other'}
                    </span>
                  </TableCell>
                  <TableCell>{getStatusBadge(feature.status || 'active')}</TableCell>
                  <TableCell>
                    <span style={{ color: COLORS.text.tertiary }}>
                      {new Date(feature.created_at!).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(feature)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.text.secondary,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(feature.id!)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.status.error,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Panel>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingFeature(null);
        }}
        title={editingFeature?.id ? 'Edit Feature' : 'New Feature'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Name
            </label>
            <Input
              value={editingFeature?.name || ''}
              onChange={(e) => setEditingFeature({ ...editingFeature, name: e.target.value })}
              placeholder="Feature name"
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Description
            </label>
            <Textarea
              value={editingFeature?.description || ''}
              onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })}
              placeholder="What does this feature do?"
              rows={3}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Screenshot (Optional)
            </label>
            <ImageUpload
              value={imageFile || editingFeature?.image_url || null}
              onChange={setImageFile}
              onPaste={() => {}}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Expected Behavior
            </label>
            <Textarea
              value={editingFeature?.expected_behavior || ''}
              onChange={(e) => setEditingFeature({ ...editingFeature, expected_behavior: e.target.value })}
              placeholder="How should it behave?"
              rows={2}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}

// ============================================
// RESEARCH TAB (simplified for brevity)
// ============================================
function ResearchTab() {
  const [research, setResearch] = useState<Research[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingResearch, setEditingResearch] = useState<Partial<Research> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchResearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await researchApi.getAll({ search: searchTerm || undefined });
      setResearch(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load research');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingResearch({
      title: '',
      summary: '',
      findings: '',
      category: 'discovery',
      status: 'pending',
      affects_production: false,
    });
    setImageFile(null);
    setShowForm(true);
  };

  const handleEdit = (item: Research) => {
    setEditingResearch(item);
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this research?')) return;
    try {
      await researchApi.delete(id);
      fetchResearch();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingResearch) return;
    try {
      const researchData = { ...editingResearch, image: imageFile };
      if (editingResearch.id) {
        await researchApi.update(editingResearch.id, researchData);
      } else {
        await researchApi.create(researchData as Research);
      }
      setShowForm(false);
      setEditingResearch(null);
      setImageFile(null);
      fetchResearch();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: any = {
      validated: 'success',
      invalidated: 'error',
      pending: 'warning',
      superseded: 'default',
      archived: 'default',
    };
    return <Badge color={colors[status] || 'default'}>{status}</Badge>;
  };

  return (
    <>
      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <div style={{ display: 'flex', gap: SPACING[12], marginBottom: SPACING[16] }}>
        <div style={{ flex: 1 }}>
          <Input
            placeholder="Search research..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
          />
        </div>
        <Button variant="secondary" onClick={fetchResearch}>
          Search
        </Button>
        <Button variant="primary" onClick={handleCreate} icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}>
          New Research
        </Button>
      </div>

      <Panel noPadding>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Title</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Production</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader align="right">Actions</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>Loading...</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : research.length === 0 ? (
              <TableRow>
                <TableCell>
                  <span style={{ color: COLORS.text.tertiary }}>No research found</span>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              research.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{item.title}</div>
                    {item.summary && (
                      <div
                        style={{
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          marginTop: SPACING[4],
                        }}
                      >
                        {item.summary.slice(0, 60)}
                        {item.summary.length > 60 ? '...' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={{ textTransform: 'capitalize', color: COLORS.text.secondary }}>
                      {item.category ? item.category.replace('_', ' ') : 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status || 'pending')}</TableCell>
                  <TableCell>
                    {item.affects_production ? (
                      <Badge color="warning">Yes</Badge>
                    ) : (
                      <Badge color="default">No</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span style={{ color: COLORS.text.tertiary }}>
                      {item.research_date
                        ? new Date(item.research_date).toLocaleDateString()
                        : new Date(item.created_at!).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ display: 'flex', gap: SPACING[8], justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(item)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.text.secondary,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <PencilIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id!)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.status.error,
                          cursor: 'pointer',
                          padding: SPACING[4],
                        }}
                      >
                        <TrashIcon style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Panel>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingResearch(null);
        }}
        title={editingResearch?.id ? 'Edit Research' : 'New Research'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Title
            </label>
            <Input
              value={editingResearch?.title || ''}
              onChange={(e) => setEditingResearch({ ...editingResearch, title: e.target.value })}
              placeholder="Research title"
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Category
            </label>
            <Select
              value={editingResearch?.category || 'discovery'}
              onChange={(e) => setEditingResearch({ ...editingResearch, category: e.target.value as any })}
              options={[
                { value: 'algorithm_tuning', label: 'Algorithm Tuning' },
                { value: 'data_analysis', label: 'Data Analysis' },
                { value: 'experiment', label: 'Experiment' },
                { value: 'discovery', label: 'Discovery' },
                { value: 'hypothesis', label: 'Hypothesis' },
                { value: 'calibration', label: 'Calibration' },
              ]}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Summary
            </label>
            <Textarea
              value={editingResearch?.summary || ''}
              onChange={(e) => setEditingResearch({ ...editingResearch, summary: e.target.value })}
              placeholder="Brief summary"
              rows={2}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Findings
            </label>
            <Textarea
              value={editingResearch?.findings || ''}
              onChange={(e) => setEditingResearch({ ...editingResearch, findings: e.target.value })}
              placeholder="What did you find?"
              rows={3}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Screenshot (Optional)
            </label>
            <ImageUpload
              value={imageFile || editingResearch?.image_url || null}
              onChange={setImageFile}
              onPaste={() => {}}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                marginBottom: SPACING[8],
              }}
            >
              Conclusion
            </label>
            <Textarea
              value={editingResearch?.conclusion || ''}
              onChange={(e) => setEditingResearch({ ...editingResearch, conclusion: e.target.value })}
              placeholder="What's the conclusion?"
              rows={2}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <input
              type="checkbox"
              id="affects_production"
              checked={editingResearch?.affects_production || false}
              onChange={(e) => setEditingResearch({ ...editingResearch, affects_production: e.target.checked })}
              style={{ width: '16px', height: '16px' }}
            />
            <label
              htmlFor="affects_production"
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.primary,
                cursor: 'pointer',
              }}
            >
              Affects Production
            </label>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default TrackingPage;
